<template>

  <div class="map-marker"
      v-if="coordinates.length > 0"
      :style="markerStyle"
      :id="id"
      :ref="id"
      v-on:mouseover="handleMouseover"
      v-on:mouseleave="handleMouseleave" />

</template>

<script>
/* global mapboxgl */
import StringBuffer from 'stringbuffer'

export default {
  name: 'MapMarker',

  data () {
    return {
      defaultIconWidth: 23,
      defaultIconHeight: 33,
      defaultMarkerType: 'location',
      popup: null,
      marker: null
    }
  },

  props: {
    id: {
      type: String,
      default: ''
    },
    coordinates: {
      type: Array,
      default: () => []
    },
    properties: {
      type: Object,
      default: () => {}
    },
    map: {
      type: Object,
      default: () => null
    },
    enablePopup: {
      type: Boolean,
      default: true
    }
  },

  computed: {
    markerStyle () {
      const width = this.properties.icon_width || this.defaultIconWidth
      const height = this.properties.icon_height || this.defaultIconHeight
      const markerType = this.properties.type || this.defaultMarkerType
      const markerImgSrc = `/img/map/marker-icon-${markerType}.png`

      return {
        display: 'block',
        backgroundSize: '100%',
        backgroundImage: `url(${markerImgSrc})`,
        width: `${width}px`,
        height: `${height}px`
      }
    },
    popupContent () {
      const content = new StringBuffer()
      const stepIndex = this.properties.step_index
      const locationName = this.properties.name
      const route = this.properties.route || {}
      const orders = this.properties.orders
      const notes = this.properties.notes
      const type = this.properties.type
      let typeForDisplay = (stepIndex) ? `<span class="step-index">(${stepIndex})</span> ` : ''
      switch (type) {
        case 'dropoff-location': typeForDisplay += 'Dropoff'; break
        case 'pickup-location': typeForDisplay += 'Pickup'; break
        case 're-route': typeForDisplay += 'Re-route'; break
        case 'route-change': typeForDisplay += 'Route Change'; break
        case 'vehicle-lot': typeForDisplay = 'Vehicle Lot'; break
      }

      // Generate content
      content.append('<div class="popup-content">')
      // Marker type and location title
      if (typeForDisplay) content.append(`<div class="location-type">${typeForDisplay}</div>`)
      if (type !== 'route-change' && locationName) {
        content.append(`<div class="location-name">${locationName}</div>`)
      }

      if (type === 'vehicle-lot') {
        const routeStatus = route.route_status

        let startTimeHeader = ''
        let startTimeToDisplay = ''
        if (routeStatus === 'CREATED' || routeStatus === 'DISPATCHED') {
          startTimeHeader = 'start route: '
          startTimeToDisplay = (route.scheduled_start_time) ? this.formatTime(route.scheduled_start_time) : ''
        } else {
          startTimeHeader = 'started route: '
          startTimeToDisplay = (route.on_delivery_at) ? this.formatTime(route.on_delivery_at) : ''
        }

        let endTimeHeader = ''
        let endTimeToDisplay = ''
        if (routeStatus === 'COMPLETED') {
          endTimeHeader = 'completed: '
          endTimeToDisplay = this.formatTime(route.completed_at)
        } else {
          endTimeHeader = 'est. complete: '
          endTimeToDisplay = (route.estimated_return_time) ? this.formatTime(route.estimated_return_time) : ''
        }
        content.append(`<div class="route-details"><span class="route-time-header">${startTimeHeader}</span><span>${startTimeToDisplay}</span></div>`)
        content.append(`<div class="route-details"><span class="route-time-header">${endTimeHeader}</span><span>${endTimeToDisplay}</span></div>`)
      }

      // Route change
      if (type === 'route-change') {
        if (notes) {
          const truncatedNotes = (notes.length > 95) ? notes.substring(0, 95) + '...' : notes
          content.append(`<div class="route-notes"><span class="location-name" style="margin-right:6px;">Details:</span>${truncatedNotes}</div>`)
        }
        // location of driver
        const coords = this.properties?.loc?.coords || {}
        const coordsToDisplay = (coords.latitude && coords.longitude) ? `lat: ${coords.latitude}, lon: ${coords.longitude}` : null
        if (coordsToDisplay) content.append(`<div class="route-change-coords"><span>${coordsToDisplay}</span></div>`)
        // route change timestamp
        const changeTimeHeader = 'changed at: '
        const changeTimeToDisplay = (this.properties.route_changed_at) ? this.formatTime(this.properties.route_changed_at) : ''
        content.append(`<div class="route-details"><span class="route-time-header">${changeTimeHeader}</span><span>${changeTimeToDisplay}</span></div>`)
      }

      // Order list (for delivery steps)
      if (orders && orders.length > 0) {
        for (const order of orders) {
          let timeHeader = ''
          let timeToDisplay = null
          switch (type) {
            case 'dropoff-location': {
              if (order.order_status === 'BEING_SCHEDULED' || order.order_status === 'SCHEDULED' || order.order_status === 'PICKED_UP' || order.order_status === 'TO_RECEIVER' || order.order_status === 'AT_RECEIVER') {
                timeHeader = 'est. arrival: '
                timeToDisplay = this.formatTime(order.estimated_receiver_arrival_time)
              } else if (order.order_status === 'DELIVERED') {
                timeHeader = 'delivered: '
                timeToDisplay = this.formatTime(order.delivered_at)
              } else if (order.order_status === 'REJECTED_ON_DELIVERY') {
                timeHeader = 'rejected: '
                timeToDisplay = this.formatTime(order.rejected_at)
              } else if (order.order_status === 'RESCHEDULED') {
                timeHeader = 'RESCHEDULED'
              }
              break
            }
            case 'pickup-location': {
              if (order.order_status === 'BEING_SCHEDULED' || order.order_status === 'SCHEDULED' || order.order_status === 'TO_SHIPPER' || order.order_status === 'AT_SHIPPER') {
                timeHeader = 'est. arrival: '
                timeToDisplay = this.formatTime(order.estimated_shipper_arrival_time)
              } else if (order.order_status === 'RESCHEDULED') {
                timeHeader = 'RESCHEDULED'
              } else if (order.picked_up_at) {
                timeHeader = 'picked-up: '
                timeToDisplay = this.formatTime(order.picked_up_at)
              }
              break
            }
          }
          if (timeToDisplay) {
            content.append(`<div class="order-details"><span class="order-number">${order.thirdparty_order_number}</span><span> - ${timeHeader}</span><span>${timeToDisplay}</span></div>`)
          } else {
            content.append(`<div class="order-details"><span class="order-number">${order.thirdparty_order_number}</span><span> - ${timeHeader}</span></div>`)
          }
        }
      }
      content.append('</div>')

      return content.toString()
    }
  },

  mounted () {
    if (this.map) {
      // Access element
      const el = this.$refs[this.id]

      // Support popup (if enabled)
      if (this.enablePopup) {
        this.popup = new mapboxgl.Popup({
          closeButton: true,
          closeOnClick: true,
          closeOnMove: false,
          offset: 20
        }).setHTML(this.popupContent)

        this.marker = new mapboxgl.Marker(el)
          .setLngLat(this.coordinates)
          .setPopup(this.popup)
          .addTo(this.map)

      // Otherwise, just render marker
      } else {
        this.marker = new mapboxgl.Marker(el)
          .setLngLat(this.coordinates)
          .addTo(this.map)
      }
    }
  },

  methods: {
    formatTime (date) {
      return (date)
        ? this.$root.utils.dateFormat(date, 'h:mma z')
        : ''
    },

    handleMouseover () {
    },
    handleMouseleave () {
    }
  }
}
</script>

<style>
  .mapboxgl-popup-close-button {
    top: 5px;
    right: 8px;
    width: 20px;
    height: 20px;
    font-size: 25px;
    color: #616161;
  }

  .mapboxgl-popup-content {
    border: 2px solid #1976D2;
    border-radius: 5px;
    color: #000000;
    padding: 11px 18px;
    width: 280px;
  }

  .popup-content .step-index {
    font-weight: 500;
    font-size: 12px;
    font-style: italic;
    color: #525252;
  }

  .popup-content .location-type {
    font-weight: 500;
    font-size: 12px;
    text-transform: uppercase !important;
    font-style: italic;
    color: #727272;
    text-align: center;
    margin-bottom: 6px;
  }

  .popup-content .location-name {
    font-weight: 600;
    font-size: 15px;
    color: #EF6C00;
    text-align: center;
    margin-bottom: 8px;
  }

  .popup-content .route-details {
    font-weight: 400;
    font-size: 13px;
    color: #727272;
    text-align: left;
    padding-top: 4px;
  }

  .popup-content .route-notes {
    font-weight: 500;
    font-size: 14px;
    text-align: left;
    color: #525252;
    padding-top: 2px;
    padding-bottom: 6px;
  }

  .popup-content .route-time-header {
    font-weight: 600;
    font-size: 14px;
    color: #525252;
  }

  .popup-content .route-change-coords {
    font-weight: 400;
    font-size: 13px;
    color: #727272;
    text-align: left;
    padding-top: 2px;
  }

  .popup-content .order-details {
    font-weight: 400;
    font-size: 13px;
    color: #727272;
    text-align: left;
    padding-top: 4px;
  }

  .popup-content .order-number {
    font-weight: 600;
    font-size: 14px;
    color: #525252;
  }
</style>
